import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { 
  CheckCircle, XCircle, Eye, FileText, 
  MapPin, Building2, Link, Award, Loader2, Hash, Globe,
  Search, Phone, Mail, User // <--- NEW ICONS
} from 'lucide-react';
import './CollegeRequests.css';

const CollegeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCollege, setSelectedCollege] = useState(null); 
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // <--- NEW STATE FOR SEARCH

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/college-requests`, {
        headers: { 'Authorization': token }
      });
      setRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch requests", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    if (!window.confirm(`Are you sure you want to ${status.toUpperCase()} this college?`)) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/admin/college-status/${id}`, 
        { status },
        { headers: { 'Authorization': token } }
      );
      
      setRequests(prev => prev.filter(req => req.id !== id));
      setSelectedCollege(null); 
    } catch (error) {
      console.error(`Failed to ${status} college`, error);
      alert("An error occurred. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // --- FILTER LOGIC ---
  const filteredRequests = requests.filter(req => {
      const search = searchTerm.toLowerCase();
      return (
          (req.collegeName && req.collegeName.toLowerCase().includes(search)) ||
          (req.collegeRegNo && req.collegeRegNo.toLowerCase().includes(search))
      );
  });

  if (loading) return <div className="cr-loading-state"><Loader2 className="cr-spin" size={24} /> Loading pending requests...</div>;

  return (
    <div className="cr-container">
      
      {/* --- SEARCH BAR --- */}
      {requests.length > 0 && (
          <div className="cr-toolbar">
              <div className="cr-search-box">
                  <Search size={18} className="cr-search-icon" />
                  <input 
                      type="text" 
                      placeholder="Search by College Name or Reg No..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                  />
              </div>
          </div>
      )}

      {requests.length === 0 ? (
        <div className="cr-empty-state">
          <CheckCircle size={56} color="#10b981" className="cr-empty-icon" />
          <h3>You're all caught up!</h3>
          <p>There are no pending institution verification requests right now.</p>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="cr-empty-state">
            <Search size={40} color="#64748b" className="cr-empty-icon" />
            <h3>No results found</h3>
            <p>No pending requests match your search criteria.</p>
        </div>
      ) : (
        <div className="cr-table-wrap">
          <table className="cr-table">
            <thead>
              <tr>
                <th>Institution Name</th>
                <th>Registration No.</th>
                <th>Location</th>
                <th>Contact Person</th>
                <th className="cr-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((college) => (
                <tr key={college.id}>
                  <td className="cr-fw-bold">
                    <div className="cr-name-cell">
                        <Building2 size={16} className="cr-row-icon" />
                        {college.collegeName}
                    </div>
                  </td>
                  <td className="cr-text-muted">{college.collegeRegNo}</td>
                  <td className="cr-text-muted">{college.city}, {college.country}</td>
                  <td className="cr-text-muted">{college.poName}</td>
                  <td className="cr-actions-col">
                    <div className="cr-action-buttons">
                        <button className="cr-btn-view" onClick={() => setSelectedCollege(college)} title="View Details">
                        <Eye size={16} /> View
                        </button>
                        <button className="cr-btn-approve" onClick={() => handleStatusUpdate(college.id, 'Approved')} title="Approve">
                        <CheckCircle size={16} />
                        </button>
                        <button className="cr-btn-reject" onClick={() => handleStatusUpdate(college.id, 'Rejected')} title="Reject">
                        <XCircle size={16} />
                        </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --- DARK PREMIUM MODAL (RENDERED VIA PORTAL) --- */}
      {selectedCollege && ReactDOM.createPortal(
        <div className="cr-modal-overlay" onClick={() => setSelectedCollege(null)}>
          <div className="cr-modal-content fade-in-up" onClick={e => e.stopPropagation()}>
            
            <div className="cr-modal-header">
              <div>
                  <h3 className="cr-modal-title">{selectedCollege.collegeName}</h3>
                  <p className="cr-modal-subtitle">Full Verification Profile</p>
              </div>
              <button className="cr-close-btn" onClick={() => setSelectedCollege(null)}>×</button>
            </div>
            
            <div className="cr-modal-body">
              
              {/* SECTION 1: General Information */}
              <h4 className="cr-section-title">General Information</h4>
              <div className="cr-detail-grid">
                <div className="cr-detail-item">
                  <span className="cr-label"><Hash size={14}/> System ID</span>
                  <span className="cr-value">{selectedCollege.systemId || 'N/A'}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label"><Hash size={14}/> Registration No.</span>
                  <span className="cr-value">{selectedCollege.collegeRegNo}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label"><Building2 size={14}/> University Name</span>
                  <span className="cr-value">{selectedCollege.universityName}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label"><Award size={14}/> Affiliation</span>
                  <span className="cr-value">{selectedCollege.affiliation || 'N/A'}</span>
                </div>
                <div className="cr-detail-item full-span">
                  <span className="cr-label"><Link size={14}/> Website</span>
                  <span className="cr-value">
                      {selectedCollege.website ? <a href={selectedCollege.website} target="_blank" rel="noreferrer" className="cr-link">{selectedCollege.website}</a> : 'N/A'}
                  </span>
                </div>
              </div>

              {/* SECTION 2: Location */}
              <h4 className="cr-section-title">Location Details</h4>
              <div className="cr-detail-grid">
                <div className="cr-detail-item full-span">
                  <span className="cr-label"><MapPin size={14}/> Street Address</span>
                  <span className="cr-value">{selectedCollege.streetAddress}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label">City</span>
                  <span className="cr-value">{selectedCollege.city}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label">State</span>
                  <span className="cr-value">{selectedCollege.state}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label">Pincode</span>
                  <span className="cr-value">{selectedCollege.pincode}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label"><Globe size={14}/> Country</span>
                  <span className="cr-value">{selectedCollege.country}</span>
                </div>
              </div>

              {/* SECTION 3: Contact & PO Details */}
              <h4 className="cr-section-title">Contact Information</h4>
              <div className="cr-detail-grid">
                <div className="cr-detail-item">
                  <span className="cr-label"><Phone size={14}/> College Phone</span>
                  <span className="cr-value">{selectedCollege.collegePhone}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label"><Mail size={14}/> College Email</span>
                  <span className="cr-value">{selectedCollege.collegeEmail}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label"><User size={14}/> Placement Officer Name</span>
                  <span className="cr-value">{selectedCollege.poName}</span>
                </div>
                <div className="cr-detail-item">
                  <span className="cr-label"><Phone size={14}/> PO Phone</span>
                  <span className="cr-value">{selectedCollege.poPhone}</span>
                </div>
                <div className="cr-detail-item full-span">
                  <span className="cr-label"><Mail size={14}/> PO Email</span>
                  <span className="cr-value">{selectedCollege.poEmail}</span>
                </div>
              </div>

              {/* SECTION 4: Documents */}
              <h4 className="cr-section-title">Verification Documents</h4>
              <div className="cr-docs-section">
                {selectedCollege.tradeLicensePath ? (
                  <a href={selectedCollege.tradeLicensePath} target="_blank" rel="noreferrer" className="cr-doc-btn primary-doc">
                    <FileText size={18} /> View Trade License <span className="cr-badge-req">Mandatory</span>
                  </a>
                ) : (
                  <div className="cr-missing-doc"><XCircle size={16}/> Missing Trade License</div>
                )}

                {selectedCollege.otherDocs && selectedCollege.otherDocs.length > 0 && (
                  <div className="cr-other-docs">
                    <p className="cr-label">Additional Documents:</p>
                    <div className="cr-doc-grid">
                        {selectedCollege.otherDocs.map((docUrl, i) => (
                        <a key={i} href={docUrl} target="_blank" rel="noreferrer" className="cr-doc-btn secondary-doc">
                            <FileText size={16} /> Document {i + 1}
                        </a>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="cr-modal-footer">
              <button 
                className="cr-footer-btn reject-btn" 
                onClick={() => handleStatusUpdate(selectedCollege.id, 'Rejected')}
                disabled={actionLoading}
              >
                <XCircle size={18}/> Reject Application
              </button>
              <button 
                className="cr-footer-btn approve-btn" 
                onClick={() => handleStatusUpdate(selectedCollege.id, 'Approved')}
                disabled={actionLoading}
              >
                <CheckCircle size={18}/> Approve & Grant Access
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CollegeRequests;